import { SegmentsMeta, PROMO_KEYS} from '@fedsim/constants';
import { groupBy  } from '@fedsim/helpers'

import { BaseMachine } from './base'

export function round(value, decimals = 2) {
  return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals)
}

export function fuzzyAmount(multipler = 10, divider = 2) {
  return Math.floor(Math.random() * multipler) / divider
}

export function getAppearanceAvg(segments, key) {
  return segments?.reduce(
    (n, curr) => {
      const val = curr.appearances.reduce(
        (n, curr) => n + curr.wrestler?.[key],
        0
      );
      return n + val
    },
    0
  );
}

export function flowFactor(position, total) {
  if (position === 0 || position === total - 1) {
    return 1.1; // Hot start and finish
  } else if (position === 1 || position === total - 2) {
    return 0.9; // Cooler second and second-to-last segments
  } else {
    return 1; // Normal flow for the rest
  }
}


export function getShowScoreModifiers(production) {
  const hasTagMatch = production?.segments?.some(segment => {
    const groups = groupBy(segment.appearances, 'groupId')

    return Object.keys(groups).every(key => groups[key].length === 2)
  });
  const hasPromo = production?.segments?.some(segment => PROMO_KEYS.includes(segment?.type))
  const hasWoman = production?.segments?.some(segment => segment?.appearances?.some(appearance => appearance.wrestler.gender === "FEMALE"));
  const hasMan = production?.segments?.some(segment => segment?.appearances?.some(appearance => appearance.wrestler.gender === "MALE"));
  // points
  const hasLowPointer = production?.segments?.some(segment => segment?.appearances?.some(appearance => appearance.wrestler.points < 50));
  const hasHighPointer = production?.segments?.some(segment => segment?.appearances?.some(appearance => appearance.wrestler.points > 80));
  const hasHighCharisma = production?.segments?.some(segment => segment?.appearances?.some(appearance => appearance.wrestler.charisma > 80));
  // champs
  const hasChampionshipMatch = production?.segments?.some(segment => segment?.championshipIds?.length > 0);
  // duration
  const duration = production?.segments?.reduce((acc, segment) => acc + segment?.duration, 0)
  const hasShortDuration = duration < production?.show?.minDuration;
  const hasLongDuration = duration > production?.show?.maxDuration;
  const hasRightDuration = duration > 0.9 * production?.show?.maxDuration && duration < production?.show?.maxDuration;

  return {
    hasTagMatch,
    hasPromo,
    hasWoman,
    hasMan,
    hasLowPointer,
    hasHighPointer,
    hasHighCharisma,
    hasChampionshipMatch,
    hasShortDuration,
    hasLongDuration,
    hasRightDuration
  }
}

// @TODO
// 20% of matches arent just "matches"
// has 1 promo
// has 1 tag match
export function generateModifierScore(production) {
  //init
  let value = 0
  // get modifiers
  const modifiers = getShowScoreModifiers(production)
  // console.log('getShowScoreModifiers', modifiers)
  const { hasWoman, hasMan, hasLowPointer, hasPromo, hasTagMatch, hasHighPointer, hasHighCharisma, hasChampionshipMatch, hasShortDuration, hasLongDuration, hasRightDuration } = modifiers
  // gender bonuses
  if (hasMan) {
    value = value + 10;
  }
  if (hasWoman) {
    value = value + 10;
  }
  if (hasWoman && hasMan) {
    value = value + 10;
  }
  // duration bonuses
  if (hasRightDuration) {
    value = value + 10;
  }
  if (hasLongDuration) {
    value = value - 10;
  }
  if (hasShortDuration) {
    value = value - 10;
  }
  //point bonuses
  if (hasLowPointer) {
    value = value + 10;
  }
  if (hasHighPointer) {
    value = value + 10;
  }
  // perks
  if (hasHighCharisma) {
    value = value + 10;
  }
  if (hasChampionshipMatch) {
    value = value + 10;
  }
  if (hasPromo) {
    value = value + 10;
  }
  if (hasTagMatch) {
    value = value + 10;
  }

  //return
  // console.log('modifiers score', value)
  return Number(value)
}

// Constants to tweak the weight of different factors
const popularityWeight = 0.7;
const charismaWeight = 0.7;
const pointsWeight = 0.6;
const moraleWeight = 0.4;
const staminaWeight = 0.2;
const scoreWeight = 60;

function predictViewers(production) {
  // generate base score
  const modifierScore = generateModifierScore(production);

  // prepare averages
  let avgPopularity = 0;
  let avgPoints = 0;
  let avgCharisma = 0;
  let avgMorale = 0;
  let avgStamina = 0;

  // loop segments and calculate averages, multiply by flow factor based on position on the card
  for (const [index, segment] of production?.segments?.entries()) {
    const factor = flowFactor(index, production.segments.length);

    // generate avgs
    avgPopularity += getAppearanceAvg([segment], "popularity") * factor;
    avgPoints += getAppearanceAvg([segment], "points") * factor;
    avgCharisma += getAppearanceAvg([segment], "charisma") * factor;
    avgMorale += getAppearanceAvg([segment], "morale") * factor;
    avgStamina += getAppearanceAvg([segment], "stamina") * factor;
  }

  // divide vals vy length of show
  avgPopularity /= production?.segments?.length || 0;
  avgCharisma /= production?.segments?.length || 0;
  avgPoints /= production?.segments?.length || 0;
  avgMorale /= production?.segments?.length || 0;
  avgStamina /= production?.segments?.length || 0;

  // Calculate the score based on the weighted factors
  const averageScores = (
    avgPopularity * popularityWeight +
    avgCharisma * charismaWeight +
    avgPoints * pointsWeight +
    avgMorale * moraleWeight +
    avgStamina * staminaWeight
  );

  const avgWeight = (averageScores * scoreWeight)
  const modifierWeight = (avgWeight * modifierScore)
  const result = (production?.show?.avgViewers || 0) + modifierWeight + avgWeight;

  // console.log('averageScores', averageScores);
  // console.log('avgWeight', avgWeight)
  // console.log('modifierWeight', modifierWeight)
  // console.log('result', result)

  return Math.round(result);
}

export class ProductionMachine extends BaseMachine {
  constructor(props) {
    const model = Object.assign({}, props)
    super(model)
    this.nextState = model
  }

  recordFuzz() {
    const values = this.nextState

    this.nextState.attendance = round(values?.attendance + fuzzyAmount(), 0)
    this.nextState.attendanceIncome = round(values?.attendanceIncome + fuzzyAmount())
    this.nextState.merchIncome = round(values?.merchIncome + fuzzyAmount())
    this.nextState.viewers = round(values?.viewers + fuzzyAmount(300, 0.3))

    return this
  }

  getSegmentsMeta(type) {
    return SegmentsMeta[type] || false
  }

  set(key, value) {
    this.nextState[key] = value
  }

  calculateViewers() {
    // Call the predictViewers function
    const scoredViewers = predictViewers(this.nextState);

    // Set the viewers to the calculated value
    this.set('viewers', scoredViewers);

    return this;
  }

  calculateStandardViewers() {
    const value = this.nextState?.show?.avgViewers || 0

    this.set('viewers', Number(value))

    return this
  }

  calculateAttendance() {
    const value = this.nextState?.show?.avgAttendance

    this.set('attendance', Number(value))

    return this
  }

  calculateMerchIncome() {
    this.calculateAttendance()
    const value = this.nextState?.show?.avgMerchPrice * (this.nextState.attendance * 0.4)

    this.set('merchIncome', Number(value))

    return this
  }

  calculateAttendanceIncome() {
    const value = this.nextState?.show?.avgAttendance * this.nextState?.show?.avgTicketPrice

    this.set('attendanceIncome', Number(value))

    return this
  }

  calculateWrestlersCost() {
    const total = this.nextState?.segments?.reduce((acc, segment) => {
      const cost = segment?.appearances?.reduce((acc, appearance) => acc + appearance?.wrestler?.cost, 0)

      return cost + acc
    }, 0)

    this.set('wrestlersCost', Number(total))

    return this
  }

  calculateSegmentsCost() {
    const total = this.nextState?.segments?.reduce((acc, segment) => {
      const cost = this.getSegmentsMeta(segment?.type)?.cost || 0

      return cost + acc
    }, 0)

    this.set('segmentsCost', Number(total))

    return this
  }

  calculateOutgoings() {
    this.calculateWrestlersCost()
    this.calculateSegmentsCost()

    const value = Number(this.nextState?.wrestlersCost) + Number(this.nextState?.segmentsCost)

    this.set('outgoings', Number(value))

    return this
  }

  calculateIncome() {
    this.calculateMerchIncome()
    this.calculateAttendanceIncome()

    const value = Number(this.nextState?.merchIncome) + Number(this.nextState?.attendanceIncome)

    this.set('income', Number(value))

    return this
  }

  calculatePayouts() {
    this.calculateIncome()
    this.calculateOutgoings()

    const value = Number(this.nextState?.income) - Number(this.nextState?.outgoings)

    this.set('payout', Number(value))

    return this
  }

  calculateSinglePayout() {
    this.calculatePayouts()

    const value = this.nextState?.payout / this.nextState?.brandIds?.length

    this.set('singlePayout', Number(value))

    return this
  }

  applyPayouts() {
    this.calculateSinglePayout()

    const value = this.nextState?.brands?.map(brand => {
      return {
        ...brand,
        balance: brand?.balance + this.nextState?.singlePayout,
      }
    })

    this.set('brands', value)

    return this
  }

  toggleComplete() {
    const value = !this.nextState?.complete

    this.set('complete', value)

    return this
  }

  calculate() {
    this.calculateViewers()
    this.calculateAttendance()
    this.calculateMerchIncome()
    this.calculateAttendanceIncome()
    this.calculateWrestlersCost()
    this.calculateSegmentsCost()
    this.calculateOutgoings()
    this.calculateIncome()
    this.calculatePayouts()
    this.calculateSinglePayout()

    return this
  }

  getState() {
    return this.nextState
  }
}

export default ProductionMachine
